'use client';

import { useEffect, useId, useRef, useState } from 'react';

import { IconButton } from '@eliadmin/core/src/components/nt/ui/icon-button';
import { cn } from '@eliadmin/core/src/utils/style-utils';

export type ShareLabels = {
  copy: string;
  /** Announced after copying („Odkaz bol skopírovaný do schránky“). */
  copied: string;
  copyFailed: string;
  facebook: string;
  email: string;
  print: string;
};

/** How long the copy button shows the check after copying. */
const COPIED_MS = 2000;

/**
 * Figma „Share“ (250:15170, mobile row 310:17731): „Zdieľať:“ and four Outline M icon buttons – copy
 * the link (link), share on Facebook (sharer URL), send by e-mail (mailto) and print. Only real
 * actions; a button whose name is missing in the administration is left out.
 */
export function ShareButtons({
  label,
  url,
  title,
  labels,
  className,
}: {
  /** „Zdieľať:“. */
  label: string;
  /** Absolute URL of the post. */
  url: string;
  title: string;
  labels: ShareLabels;
  className?: string;
}) {
  const labelId = useId();
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);

  async function copyLink() {
    clearTimeout(timer.current);
    try {
      await navigator.clipboard.writeText(url || window.location.href);
      setCopyState('copied');
    } catch {
      setCopyState('failed');
    }
    timer.current = setTimeout(() => setCopyState('idle'), COPIED_MS);
  }

  const facebookHref = url
    ? `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`
    : '';
  const emailHref = url
    ? `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(url)}`
    : '';

  return (
    <div
      role="group"
      aria-labelledby={label ? labelId : undefined}
      className={cn('flex items-center gap-2', className)}
    >
      {label ? (
        <span id={labelId} className="nt-body-m text-[#363D36] max-md:flex-1 max-md:text-[12px]">
          {label}
        </span>
      ) : null}
      {labels.copy ? (
        <IconButton
          icon={copyState === 'copied' ? 'check' : 'link'}
          label={labels.copy}
          onClick={() => void copyLink()}
        />
      ) : null}
      {labels.facebook && facebookHref ? (
        <IconButton asChild icon="facebook-logo" label={labels.facebook}>
          <a href={facebookHref} target="_blank" rel="noopener noreferrer" />
        </IconButton>
      ) : null}
      {labels.email && emailHref ? (
        <IconButton asChild icon="envelope-simple" label={labels.email}>
          <a href={emailHref} />
        </IconButton>
      ) : null}
      {labels.print ? (
        <IconButton icon="printer" label={labels.print} onClick={() => window.print()} />
      ) : null}
      <span role="status" className="sr-only">
        {copyState === 'copied' ? labels.copied : copyState === 'failed' ? labels.copyFailed : ''}
      </span>
    </div>
  );
}
